/**
 * Issue #1681: localStorage marker for in-flight background rescore jobs, so
 * a completion toast survives the facilitator navigating away from
 * ScenarioAuthoringPage — `RescoreOfferModal`'s `onClose` always navigates
 * (`ScenarioAuthoringPage.jsx` ~4401-4403), unmounting the page and its local
 * poll before a multi-minute drain can finish.
 *
 * Pure read/write helpers — no React/DOM beyond `localStorage` — so the
 * marker-array logic (cap, de-dup, per-user scoping, terminal-status
 * decision) is unit-testable without jsdom (frontend vitest has none — see
 * docs/subsystems/testing.md / reference_frontend_no_jsdom). Mirrors the
 * `scenarioQualityChecks.js` dismissal-layer precedent (localStorage helpers
 * tested via `vi.stubGlobal('localStorage', ...)`).
 *
 * `useRescoreJobPoller.js` (mounted in `ToastProvider`, not a page component —
 * #1681 plan review, gemini P2: a page-scoped poller misses a `returnTo`
 * redirect that bypasses `ScenariosPageWorking` entirely) is the only
 * consumer of `getPendingRescoreJobs`/`removePendingRescoreJob`; the marker
 * is written by `ScenarioAuthoringPage.jsx`'s `handleRescore`.
 */

import { formatRescoreCompletionToast } from './rescoreJobView.js';

const STORAGE_KEY = 'rescore_pending_jobs';
// Bounds growth from a facilitator editing several scenarios in a row —
// arbitrary but low-risk (worst case: an old marker is dropped, at most one
// completion toast missed). Not worth a design review (#1681 spec Unknown #3).
const MAX_PENDING_JOBS = 5;

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_err) {
    return [];
  }
}

function writeAll(jobs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch (_err) {
    // localStorage full/unavailable — silently degrade (matches
    // scenarioQualityChecks.js's dismissal-layer precedent). Worst case: the
    // completion toast is missed, the rescore itself already ran server-side.
  }
}

function sameJob(a, b) {
  // String() both sides: route params arrive as strings, API payloads as
  // numbers — strict === on mixed types broke dedup/removal (mega62 review).
  return String(a.organizationId) === String(b.organizationId)
    && String(a.scenarioId) === String(b.scenarioId)
    && String(a.userId) === String(b.userId);
}

/**
 * Record a newly-kicked-off rescore job so it can be checked on next visit —
 * written right after a successful kickoff POST (never on a kickoff failure,
 * so there's no orphaned pending-job state to poll against — #1681 spec
 * Modal/Async State Flows).
 *
 * @param {{organizationId:*, scenarioId:number, userId:*, startedAt?:string}} job
 */
export function addPendingRescoreJob({ organizationId, scenarioId, userId, startedAt }) {
  const entry = { organizationId, scenarioId, userId, startedAt: startedAt || new Date().toISOString() };
  const jobs = readAll().filter((j) => !sameJob(j, entry));
  jobs.push(entry);
  writeAll(jobs.slice(-MAX_PENDING_JOBS));
}

/**
 * All pending markers for the given user — scoped so a shared browser/device
 * never surfaces (or silently clears) a different logged-in user's pending
 * job (#1681 plan review, gemini P3).
 *
 * @param {*} userId
 * @returns {Array<{organizationId:*, scenarioId:number, userId:*, startedAt:string}>}
 */
export function getPendingRescoreJobs(userId) {
  // Same type-normalization as sameJob(): a string-vs-number userId mismatch
  // would silently hide the user's own pending jobs (mega62 delta review).
  return readAll().filter((j) => String(j.userId) === String(userId));
}

/**
 * Remove a single marker — call only once its job has reached a confirmed
 * terminal (or terminal-unknown/idle-after-TTL-lapse) status, never
 * speculatively (#1681 spec: never clear state before the dependent async
 * operation resolves).
 */
export function removePendingRescoreJob({ organizationId, scenarioId, userId }) {
  const jobs = readAll().filter((j) => !sameJob(j, { organizationId, scenarioId, userId }));
  writeAll(jobs);
}

/**
 * Pure decision function: given a polled `GET /:id/rescore-status` response
 * for a pending job, decide what `useRescoreJobPoller` should do next.
 *
 *   - 'running'          → keep polling, marker stays.
 *   - 'idle'              → the Redis progress-key TTL lapsed before the next
 *                            visit (or the job was never known to this
 *                            instance). The outcome is genuinely lost — the
 *                            rescore itself already completed and wrote to
 *                            `scores` — drop the marker silently, no toast
 *                            (#1681 spec Edge case).
 *   - 'done' / 'failed'   → terminal; toast (via the unchanged
 *                            `formatRescoreCompletionToast`) and drop.
 *
 * @param {{status:string, total?:number, done?:number, rescored?:number, failed?:number}|null} status
 * @returns {{action:'keep'|'drop-silent'|'toast-and-drop', toast?:{message:string, type:string}}}
 */
export function deriveRescorePendingJobAction(status) {
  if (!status) return { action: 'keep' };
  if (status.status === 'idle') return { action: 'drop-silent' };
  if (status.status === 'running') return { action: 'keep' };
  const toast = formatRescoreCompletionToast(status);
  return toast ? { action: 'toast-and-drop', toast } : { action: 'drop-silent' };
}
